/**
 * CADModelViewer - Three.js 3D model viewer for GLB files
 *
 * Displays a CAD model using React Three Fiber with orbit controls,
 * grid, wireframe toggle, and auto-fit camera.
 */

import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import {
  OrbitControls,
  Grid,
  Center,
  type OnCenterCallbackProps,
} from '@react-three/drei';
import {
  Grid3x3 as GridIcon,
  Grid2x2X as GridOffIcon,
  Box as WireframeIcon,
  Focus as FitIcon,
  RefreshCw as ResetIcon,
  CircleAlert as ErrorIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { Spinner } from '@/components/ui/spinner';
import * as THREE from 'three';
import { CADMarker } from '../../types/cad';
import {
  type CADPosition,
  type CADCenterOffset,
  createCADCenterOffset,
} from '../../utils/cadCoordinates';
import { THICKNESS_BACKGROUNDS, type ThicknessBackground } from './model-viewer/thicknessPresentation';
import { Model, LoadingFallback } from './model-viewer/Model';
import { MarkerOverlay } from './model-viewer/MarkerOverlay';
import { FocusCameraOnPoint } from './model-viewer/cameraFit';
import { ViewerErrorBoundary } from './model-viewer/ViewerErrorBoundary';
import { useGlbModelLoader } from './model-viewer/useGlbObjectUrl';

export { Model, raycastCADModelPoint } from './model-viewer/Model';

const CENTER_OFFSET_EPSILON = 1e-6;

interface CADModelViewerProps {
  glbUrl: string | null;
  filename?: string;
  markers?: CADMarker[];
  /** Keep colors embedded in the GLB (used by the face-level thickness view). */
  preserveMaterialColors?: boolean;
  /** Thickness-only background controls and readable judgment shading. */
  thicknessPresentation?: boolean;
  /** Reposition the camera around this CAD-space coordinate. */
  focusPoint?: CADPosition | null;
  /**
   * Fired when a marker sphere is clicked. Used by DRBFM↔CAD linker (TG #673)
   * to open the CADMarkerDetailPanel side drawer.
   */
  onMarkerClick?: (marker: CADMarker, index: number) => void;
  /**
   * Enable shape-point selection and show the selection hint.
   * Marker interaction takes precedence when onMarkerClick is also supplied.
   */
  selectionMode?: boolean;
  /** Fired with a point in the original CAD coordinate system. */
  onModelPointClick?: (position: CADPosition) => void;
}

const CADModelViewer: React.FC<CADModelViewerProps> = ({
  glbUrl,
  filename,
  markers = [],
  preserveMaterialColors = false,
  thicknessPresentation = false,
  focusPoint = null,
  onMarkerClick,
  selectionMode = false,
  onModelPointClick,
}) => {
  const { t } = useTranslation();
  const [background, setBackground] = useState<ThicknessBackground>('lightGray');
  const [orbitTarget, setOrbitTarget] = useState(() => new THREE.Vector3());
  const [wireframe, setWireframe] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [modelRadius, setModelRadius] = useState<number | null>(null);
  const [centerOffset, setCenterOffset] = useState<CADCenterOffset>(
    () => createCADCenterOffset(0, 0, 0),
  );
  const [isCentered, setIsCentered] = useState(false);
  // Marker clicks use R3F propagation, so keep native canvas selection off
  // when both interaction APIs are supplied.
  const effectiveSelectionMode = selectionMode && !onMarkerClick;

  const { blobUrl, loading, loadError, markLoaded, retry } = useGlbModelLoader({
    glbUrl,
    onLoadStart: () => {
      setModelRadius(null);
      setOrbitTarget(new THREE.Vector3());
      setCenterOffset(createCADCenterOffset(0, 0, 0));
      setIsCentered(false);
    },
    onClear: () => setIsCentered(false),
  });

  const handleFit = () => {
    setOrbitTarget(new THREE.Vector3());
    setFitTrigger((v) => v + 1);
  };
  const handleReset = () => {
    setWireframe(false);
    setShowGrid(true);
    handleFit();
  };

  const displayedFocusPoint = useMemo<CADPosition | null>(
    () => focusPoint
      ? [
          focusPoint[0] + centerOffset[0],
          focusPoint[1] + centerOffset[1],
          focusPoint[2] + centerOffset[2],
        ]
      : null,
    [centerOffset, focusPoint],
  );
  useEffect(() => {
    // A null selection removes markers, not the user's current inspection view.
    // Keep the target prop identity stable so OrbitControls also retains panning.
    if (displayedFocusPoint) {
      setOrbitTarget(new THREE.Vector3(...displayedFocusPoint));
    } else if (!thicknessPresentation) {
      setOrbitTarget(new THREE.Vector3());
    }
  }, [displayedFocusPoint, thicknessPresentation]);

  const backgroundColor = thicknessPresentation ? THICKNESS_BACKGROUNDS[background] : undefined;
  const lightBackground = thicknessPresentation && (background === 'white' || background === 'lightGray');
  const readableJudgments = thicknessPresentation && preserveMaterialColors;

  // drei's Center re-runs its layout effect whenever onCentered changes.
  // Keeping this callback stable prevents a viewer action from starting a
  // Center -> state update -> render loop that locks up the browser. The
  // epsilon guard also suppresses harmless bounding-box float drift if Center
  // recalculates for another reason.
  const handleCentered = useCallback(({ center }: OnCenterCallbackProps) => {
    const nextOffset = createCADCenterOffset(-center.x, -center.y, -center.z);
    setCenterOffset((currentOffset) => (
      Math.abs(currentOffset[0] - nextOffset[0]) <= CENTER_OFFSET_EPSILON
      && Math.abs(currentOffset[1] - nextOffset[1]) <= CENTER_OFFSET_EPSILON
      && Math.abs(currentOffset[2] - nextOffset[2]) <= CENTER_OFFSET_EPSILON
        ? currentOffset
        : nextOffset
    ));
    setIsCentered(true);
  }, []);

  if (!glbUrl) {
    return (
      <div className="flex h-full items-center justify-center rounded-md bg-muted">
        <p className="text-muted-foreground">
          {t('cad.viewer.selectModel')}
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 rounded-md cad-viewer-surface p-6">
        <ErrorIcon className="size-12 text-destructive" />
        <p className="text-center text-sm text-destructive">
          {loadError}
        </p>
        <Button
          size="sm"
          variant="outline"
          className="border-white/25 bg-transparent text-white/70 hover:bg-white/10 hover:text-white"
          onClick={retry}
        >
          {t('cad.viewer.retry')}
        </Button>
      </div>
    );
  }

  return (
    <ViewerErrorBoundary
      onReset={handleReset}
      loadFailedLabel={t('cad.viewer.loadFailed')}
      retryLabel={t('cad.viewer.retry')}
      unknownErrorLabel={t('common.unknown')}
    >
    <div className="relative h-full overflow-hidden rounded-md cad-viewer-surface" style={{ backgroundColor }}>
      {/* Toolbar */}
      <div className="absolute right-2 top-2 z-10 flex max-w-[calc(100%-1rem)] flex-wrap items-center justify-end gap-1 rounded-md bg-white/90 px-1">
        {thicknessPresentation && (
          <select
            aria-label={t('cad.viewer.background.label')}
            value={background}
            onChange={(event) => setBackground(event.target.value as ThicknessBackground)}
            className="h-8 rounded border border-slate-300 bg-white px-1 text-xs text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            {(Object.keys(THICKNESS_BACKGROUNDS) as ThicknessBackground[]).map((key) => (
              <option key={key} value={key}>{t(`cad.viewer.background.${key}`)}</option>
            ))}
          </select>
        )}
        <TooltipIconButton
          size="icon-sm"
          label={showGrid ? t('cad.viewer.hideGrid') : t('cad.viewer.showGrid')}
          onClick={() => setShowGrid(!showGrid)}
          className="text-slate-700 hover:bg-black/5 hover:text-slate-900"
          icon={showGrid ? <GridIcon /> : <GridOffIcon />}
        />
        <TooltipIconButton
          size="icon-sm"
          label={wireframe ? t('cad.viewer.showSolid') : t('cad.viewer.showWireframe')}
          onClick={() => setWireframe(!wireframe)}
          className="text-slate-700 hover:bg-black/5 hover:text-slate-900"
          icon={<WireframeIcon className={wireframe ? 'text-primary' : undefined} />}
        />
        <TooltipIconButton
          size="icon-sm"
          label={t('cad.viewer.fit')}
          onClick={handleFit}
          className="text-slate-700 hover:bg-black/5 hover:text-slate-900"
          icon={<FitIcon />}
        />
        <TooltipIconButton
          size="icon-sm"
          label={t('cad.viewer.reset')}
          onClick={handleReset}
          className="text-slate-700 hover:bg-black/5 hover:text-slate-900"
          icon={<ResetIcon />}
        />
      </div>

      {effectiveSelectionMode && (
        <p className="absolute left-2 top-2 z-10 rounded-md bg-primary/90 px-2.5 py-1 text-xs font-semibold text-white">
          {t('cad.viewer.selectPartPoint', '部位として登録する位置をクリック')}
        </p>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-center">
          <Spinner size="lg" label="" className="text-slate-500" />
          <p className="mt-1 block rounded bg-black/60 px-2 text-xs text-white">
            {t('cad.viewer.loading')}
          </p>
        </div>
      )}

      {/* Filename label */}
      {filename && (
        <p className="absolute bottom-2 left-2 z-10 rounded-sm bg-black/50 px-2 py-0.5 text-xs text-white/70">
          {filename}
        </p>
      )}

      <Canvas camera={{ position: [500, 500, 500], fov: 50, near: 0.1, far: 100000 }}>
        {readableJudgments ? (
          <>
            <ambientLight intensity={Math.PI * 0.8} />
            <directionalLight position={[1000, 1000, 500]} intensity={0.9} />
            <directionalLight position={[-500, -500, -500]} intensity={0.35} />
          </>
        ) : (
          <>
            <ambientLight intensity={0.6} />
            <directionalLight position={[1000, 1000, 500]} intensity={1.2} />
            <directionalLight position={[-500, 500, -500]} intensity={0.4} />
            <directionalLight position={[0, -500, 0]} intensity={0.2} />
            <hemisphereLight args={['#b1e1ff', '#2a2a3a', 0.5]} />
          </>
        )}

        <Suspense fallback={<LoadingFallback />}>
          {blobUrl && (
          <Center
            key={blobUrl}
            onCentered={handleCentered}
          >
            <Model
              url={blobUrl}
              wireframe={wireframe}
              preserveMaterialColors={preserveMaterialColors}
              thicknessPresentation={thicknessPresentation}
              onLoaded={markLoaded}
              onModelRadius={setModelRadius}
              fitTrigger={fitTrigger}
              centerOffset={centerOffset}
              selectionMode={effectiveSelectionMode && isCentered}
              onModelPointClick={onModelPointClick}
            />
          </Center>
          )}
          {markers.length > 0 && modelRadius !== null && (
            <group position={centerOffset}>
              <MarkerOverlay markers={markers} modelRadius={modelRadius} onMarkerClick={onMarkerClick} />
            </group>
          )}
        </Suspense>

        {showGrid && (
          <Grid
            args={[10000, 10000]}
            cellSize={100}
            cellColor={lightBackground ? '#aeb5bf' : '#3a3a5a'}
            sectionSize={500}
            sectionColor={lightBackground ? '#7b8492' : '#5a5a7a'}
            fadeDistance={5000}
            position={[0, -0.5, 0]}
          />
        )}

        <FocusCameraOnPoint point={displayedFocusPoint} modelRadius={modelRadius} />
        <OrbitControls
          makeDefault
          enableDamping
          dampingFactor={0.1}
          target={orbitTarget}
        />
      </Canvas>
    </div>
    </ViewerErrorBoundary>
  );
};

export default CADModelViewer;
